import React, {createContext, useContext, useMemo} from 'react';

import {Ref as CustomRunColorsRef} from '../../../state/views/customRunColors/types';
import {Ref as CustomRunNamesRef} from '../../../state/views/customRunNames/types';
import {Ref as PanelBankConfigRef} from '../../../state/views/panelBankConfig/types';
import {Ref as RunSetRef} from '../../../state/views/runSet/types';
import {Ref as SemanticLegendRef} from '../../../state/views/semanticLegend/types';
import {Ref as WorkspaceSettingsRef} from '../../../state/views/workspaceSettings/types';

type PanelsSharedRefsContextProps = {
  children: React.ReactNode;
  customRunColorsRef: CustomRunColorsRef;
  customRunNamesRef?: CustomRunNamesRef;
  runSetRefs: RunSetRef[];
  // these are optional because they only exist in workspaces
  panelBankConfigRef?: PanelBankConfigRef;
  semanticLegendRef?: SemanticLegendRef;
  workspaceSettingsRef?: WorkspaceSettingsRef;
};

type PanelsSharedRefsContextValue = Omit<
  PanelsSharedRefsContextProps,
  'children'
>;

const PanelsSharedRefsContext = createContext<
  PanelsSharedRefsContextValue | undefined
>(undefined);

export const PanelsSharedRefsContextProvider = ({
  children,
  customRunColorsRef,
  customRunNamesRef,
  panelBankConfigRef,
  runSetRefs,
  semanticLegendRef,
  workspaceSettingsRef,
}: PanelsSharedRefsContextProps) => {
  /**
   * Don't include anything that isn't refs in this context. Unpacked refs aren't always stable and can cause render thrash downstream.
   */
  const value = useMemo(
    () => ({
      customRunColorsRef,
      customRunNamesRef,
      panelBankConfigRef,
      runSetRefs,
      semanticLegendRef,
      workspaceSettingsRef,
    }),
    [
      customRunColorsRef,
      customRunNamesRef,
      panelBankConfigRef,
      runSetRefs,
      semanticLegendRef,
      workspaceSettingsRef,
    ]
  );
  return (
    <PanelsSharedRefsContext.Provider value={value}>
      {children}
    </PanelsSharedRefsContext.Provider>
  );
};

/**
 * This context provides access to refs that are shared across multiple panels.
 * Unlike WorkspaceRefsContext, it's safe to use in both reports and workspaces.
 */
export const usePanelsSharedRefsContext = () => {
  const value = useContext(PanelsSharedRefsContext);
  if (value == null) {
    throw new Error(
      'usePanelsSharedRefsContext must be used within a PanelsSharedRefsContext'
    );
  }
  return value;
};
